import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RequestTopic, submitContactRequest } from '../publicApi';
import { SITE_EVENTS, track } from '../tracking';
import HoneypotField from './HoneypotField';
import { FORM_TOPICS } from '../topics';
import { FRAGEN, beantwortete, hatFragebogen } from '../fragebogen';
import { useContact } from '../SiteContent';
import T from './EditableText';

interface ContactFormProps {
  /** Vorauswahl des Anliegens — je nach Seite bzw. `?thema=` im Link. */
  topic?: RequestTopic;
  /**
   * Auswahlfeld ausblenden. Nur sinnvoll, wo das Anliegen eindeutig ist
   * (Initiativbewerbung auf der Karriereseite).
   */
  lockTopic?: boolean;
  /** Textschlüssel für die Beschriftung des Absende-Buttons. */
  submitLabelKey?: string;
  /**
   * Platzhalter des Nachrichtenfelds.
   *
   * Bleibt bewusst ein fester Text: Platzhalter stehen in einem Attribut und
   * lassen sich im Bearbeiten-Modus, der an der sichtbaren Stelle hängt, nicht
   * anfassen. Dasselbe gilt für die übrigen Feldbeschriftungen dieses Formulars.
   */
  messagePlaceholder?: string;
}

interface FormValues {
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  nachricht: string;
}

const EMPTY: FormValues = { vorname: '', nachname: '', email: '', telefon: '', nachricht: '' };

/**
 * Kontakt-/Bewerbungsformular. Die Anfrage landet direkt im Posteingang der
 * Anwendung — die Mitarbeiter sehen sie unter „Anfragen".
 */
const ContactForm: React.FC<ContactFormProps> = ({
  topic = 'CONTACT',
  lockTopic = false,
  submitLabelKey = 'formular.absenden',
  messagePlaceholder = 'Ihre Nachricht',
}) => {
  const { pathname } = useLocation();
  const contact = useContact();
  const [values, setValues] = useState<FormValues>(EMPTY);
  const [selectedTopic, setSelectedTopic] = useState<RequestTopic>(topic);
  const [antworten, setAntworten] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');
  const [gesundheitsEinwilligung, setGesundheitsEinwilligung] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wechselt die Vorauswahl (anderer Link, andere Seite), zieht das Feld nach.
  useEffect(() => setSelectedTopic(topic), [topic]);

  // Wer erst nach Betreuung fragt und dann auf „Bewerbung" wechselt, hat seine
  // Antworten schon eingegeben. Ohne das hier würden sie mitgeschickt — und der
  // Pflegegrad einer fremden Person hinge an einer Bewerbung. Beim Wechsel auf
  // ein Anliegen ohne Fragebogen fallen sie deshalb weg.
  const fragebogenSichtbar = hatFragebogen(selectedTopic);
  useEffect(() => {
    if (!fragebogenSichtbar) {
      setAntworten({});
      setGesundheitsEinwilligung(false);
    }
  }, [fragebogenSichtbar]);

  const update = (field: keyof FormValues) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Ohne Einwilligung keine Gesundheitsangaben — und zwar bevor sie das Gerät
    // verlassen. Wer die Fragen unbeantwortet lässt, wird nicht gefragt.
    const antwortenGegeben = Object.keys(beantwortete(antworten)).length > 0;
    if (fragebogenSichtbar && antwortenGegeben && !gesundheitsEinwilligung) {
      setError(
        'Bitte bestätigen Sie die Einwilligung zu Ihren freiwilligen Angaben — oder lassen Sie die Fragen offen.',
      );
      return;
    }

    setSending(true);
    setError(null);
    try {
      await submitContactRequest({
        firstName: values.vorname,
        lastName: values.nachname,
        email: values.email,
        phone: values.telefon,
        message: values.nachricht,
        topic: selectedTopic,
        sourcePath: pathname,
        website: honeypot,
        details: fragebogenSichtbar ? beantwortete(antworten) : undefined,
      });
      setValues(EMPTY);
      setAntworten({});
      setGesundheitsEinwilligung(false);
      setSent(true);
      // Nur dass und mit welchem Anliegen — nie, was drinstand.
      track(SITE_EVENTS.anfrageGesendet, { thema: selectedTopic, seite: pathname });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="form-success" role="status">
        <h3 className="h-card" style={{ marginBottom: 8 }}>
          <T k="formular.danke.titel" />
        </h3>
        <p style={{ lineHeight: 1.65, marginBottom: 16 }}>
          <T k="formular.danke.text" />{' '}
          <a href={contact.phoneHref}>{contact.phoneLabel}</a>.
        </p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSent(false)}>
          <T k="formular.danke.cta" />
        </button>
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate={false}>
      {!lockTopic && (
        <label className="field-label">
          <T k="formular.thema.label" />
          <select
            className="field"
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value as RequestTopic)}
          >
            {FORM_TOPICS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="form-row">
        <input
          className="field"
          placeholder="Vorname"
          autoComplete="given-name"
          aria-label="Vorname"
          required
          value={values.vorname}
          onChange={update('vorname')}
        />
        <input
          className="field"
          placeholder="Nachname"
          autoComplete="family-name"
          aria-label="Nachname"
          required
          value={values.nachname}
          onChange={update('nachname')}
        />
      </div>
      <input
        className="field"
        type="email"
        placeholder="E-Mail"
        autoComplete="email"
        aria-label="E-Mail"
        required
        value={values.email}
        onChange={update('email')}
      />
      <input
        className="field"
        type="tel"
        placeholder="Telefon (optional)"
        autoComplete="tel"
        aria-label="Telefon"
        value={values.telefon}
        onChange={update('telefon')}
      />
      {fragebogenSichtbar && (
        <fieldset className="fragebogen">
          <legend className="fragebogen__legend">
            Damit wir Ihnen gleich konkret antworten können
            <span className="fragebogen__freiwillig">Alle Angaben freiwillig</span>
          </legend>
          {FRAGEN.map((frage) =>
            frage.optionen ? (
              <div key={frage.key} className="fragebogen__frage">
                {/* Kein <fieldset> je Frage: Der ganze Block ist schon eines, und
                    verschachtelte Legenden liest kaum ein Vorleseprogramm sinnvoll
                    vor. role="radiogroup" mit Verweis auf die Frage tut dasselbe. */}
                <span className="fragebogen__label" id={`frage-${frage.key}`}>
                  {frage.label}
                </span>
                <div className="fragebogen__optionen" role="radiogroup" aria-labelledby={`frage-${frage.key}`}>
                  {frage.optionen.map((option) => {
                    const gewaehlt = antworten[frage.key] === option.wert;
                    return (
                      <label key={option.wert} className="fragebogen__option">
                        <input
                          type="radio"
                          name={frage.key}
                          value={option.wert}
                          checked={gewaehlt}
                          onChange={() =>
                            setAntworten((prev) => ({ ...prev, [frage.key]: option.wert }))
                          }
                          // Nochmal auf die gewählte Antwort tippen hebt sie auf. Ohne das
                          // ließe sich eine versehentliche Angabe nicht mehr zurücknehmen —
                          // ein Radio kennt von sich aus keinen Weg zurück, und „Keine
                          // Angabe" als eigene Schaltfläche wollten wir gerade loswerden.
                          onClick={() => {
                            if (gewaehlt) setAntworten((prev) => ({ ...prev, [frage.key]: '' }));
                          }}
                        />
                        <span title={gewaehlt ? 'Nochmal tippen hebt die Angabe auf' : undefined}>
                          {option.kurz ?? option.wert}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : (
              <label key={frage.key} className="fragebogen__frage">
                <span className="fragebogen__label">{frage.label}</span>
                <input
                  className="field"
                  type="text"
                  placeholder={frage.platzhalter}
                  value={antworten[frage.key] ?? ''}
                  onChange={(event) =>
                    setAntworten((prev) => ({ ...prev, [frage.key]: event.target.value }))
                  }
                />
                {frage.hinweis && <span className="fragebogen__hinweis">{frage.hinweis}</span>}
              </label>
            ),
          )}

          {/*
            Der Pflegegrad ist eine Angabe über die Gesundheit und damit eine
            besondere Kategorie nach Art. 9 Abs. 1 DSGVO. Die verarbeitet man
            nicht auf ein berechtigtes Interesse hin, sondern nur mit
            ausdrücklicher Einwilligung (Art. 9 Abs. 2 lit. a) — und
            „ausdrücklich" heißt: eine eigene Handlung, kein stillschweigendes
            Absenden.

            Deshalb steht das Häkchen hier und nicht am ganzen Formular: Es
            erscheint nur, wo überhaupt nach Gesundheitsangaben gefragt wird,
            und es wird nur verlangt, wenn tatsächlich eine Antwort gegeben
            wurde. Wer den Fragebogen überspringt, sieht keine zusätzliche
            Hürde vor einer gewöhnlichen Anfrage.
          */}
          <div className="fragebogen__einwilligung">
            <input
              id="fragebogen-einwilligung"
              type="checkbox"
              checked={gesundheitsEinwilligung}
              onChange={(e) => setGesundheitsEinwilligung(e.target.checked)}
            />
            <span>
              {/* Der Verweis steht außerhalb des <label>: darin läge, würde ein
                  Tipp darauf zusätzlich das Häkchen umschalten. */}
              <label htmlFor="fragebogen-einwilligung">
                Ich willige ein, dass die oben freiwillig gemachten Angaben — einschließlich einer
                Angabe zum Pflegegrad — zur Beratung verarbeitet werden.
              </label>{' '}
              Die Einwilligung ist jederzeit widerrufbar; Einzelheiten in der{' '}
              <Link to="/datenschutz">Datenschutzerklärung</Link>.
            </span>
          </div>
        </fieldset>
      )}

      <textarea
        className="field"
        rows={4}
        placeholder={messagePlaceholder}
        aria-label={messagePlaceholder}
        required
        value={values.nachricht}
        onChange={update('nachricht')}
      />

      <HoneypotField value={honeypot} onChange={setHoneypot} />

      <button type="submit" className="btn btn--primary" disabled={sending}>
        {sending ? <T k="formular.sendet" /> : <T k={submitLabelKey} />}
      </button>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <p className="form-hint">
        <T k="formular.hinweis" />
      </p>
    </form>
  );
};

export default ContactForm;
